import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Tabs,
    Space,
    Button,
    Tag,
    Spin,
    message,
    Tooltip,
    Alert,
    Dropdown,
    Modal,
} from 'antd';
import type { MenuProps } from 'antd';
import {
    CloseOutlined,
    TeamOutlined,
    BellOutlined,
    MoreOutlined,
    DeleteOutlined,
    ExportOutlined,
} from '@ant-design/icons';
import { Case, CaseStatus, getCaseById, deleteCase, getCaseCollaborators } from '../../services/case';
import { getUserInfo } from '../../utils/auth';
import CaseInfoTab from '../../pages/Case/components/CaseDetail/CaseInfoTab';
import CaseFollowUpTab from '../../pages/Case/components/CaseDetail/CaseFollowUpTab';
import CaseMilestoneTab from '../../pages/Case/components/CaseDetail/CaseMilestoneTab';
import CaseFinanceTab from '../../pages/Case/components/CaseDetail/CaseFinanceTab';
import CaseAttachmentTab from '../../pages/Case/components/CaseDetail/CaseAttachmentTab';
import CaseLeadTab from '../../pages/Case/components/CaseDetail/CaseLeadTab';
import InviteCollaboratorModal from '../../pages/Case/components/CaseDetail/InviteCollaboratorModal';
import SetReminderModal from '../../pages/Case/components/CaseDetail/SetReminderModal';
import './styles.css';

const { TabPane } = Tabs;

interface CaseDrawerProps {
    caseId: number | null;
    visible: boolean;
    onClose: () => void;
    onUpdate?: () => void;
    onDelete?: () => void;
}

const CaseDrawer: React.FC<CaseDrawerProps> = ({
    caseId,
    visible,
    onClose,
    onUpdate,
    onDelete,
}) => {
    const [loading, setLoading] = useState(false);
    const [caseData, setCaseData] = useState<Case | null>(null);
    const [activeTab, setActiveTab] = useState('info');
    const [currentUserId, setCurrentUserId] = useState<number | null>(null);
    const [isCollaborator, setIsCollaborator] = useState(false);
    const [inviteModalVisible, setInviteModalVisible] = useState(false);
    const [reminderModalVisible, setReminderModalVisible] = useState(false);
    const [loadedTabs, setLoadedTabs] = useState<Set<string>>(new Set(['info']));

    // Load case details
    const loadCaseDetails = async () => {
        if (!caseId) return;

        setLoading(true);
        try {
            const data = await getCaseById(caseId);
            setCaseData(data);
        } catch (error: any) {
            if (error.response?.status === 404) {
                message.error('案件不存在');
                onClose();
            } else if (error.response?.status === 403) {
                message.error('无权限访问此案件');
                onClose();
            } else {
                message.error('加载案件详情失败');
            }
        } finally {
            setLoading(false);
        }
    };

    // Load current user
    useEffect(() => {
        const userInfo = getUserInfo();
        if (userInfo) {
            setCurrentUserId(userInfo.id);
        }
    }, []);

    // Load case details when drawer opens
    useEffect(() => {
        if (visible && caseId) {
            loadCaseDetails();
            setActiveTab('info');
            setLoadedTabs(new Set(['info']));
        }
    }, [visible, caseId]);

    // Check if user is collaborator
    useEffect(() => {
        const checkCollaborator = async () => {
            if (!caseData?.id || !currentUserId) {
                setIsCollaborator(false);
                return;
            }
            try {
                const collaborators = await getCaseCollaborators(caseData.id);
                const isCollab = collaborators.some(c => c.user_id === currentUserId);
                setIsCollaborator(isCollab);
            } catch (error) {
                console.error('Failed to check collaborator status', error);
                setIsCollaborator(false);
            }
        };
        checkCollaborator();
    }, [caseData?.id, currentUserId]);

    // Permission checks
    const permissions = caseData?.permissions;
    const canView = permissions?.can_view !== undefined ? permissions.can_view : true;
    const canEdit = permissions?.can_edit !== undefined ? permissions.can_edit : false;
    const canDelete = permissions?.can_delete !== undefined ? permissions.can_delete : false;
    const canInvite = permissions?.can_invite !== undefined ? permissions.can_invite : false;
    const canManageFinance = permissions?.can_manage_finance !== undefined ? permissions.can_manage_finance : false;

    // Handle delete case
    const handleDelete = () => {
        if (!caseData) return;

        Modal.confirm({
            title: '确认删除',
            content: `确定要删除案件"${caseData.case_name}"吗？此操作不可恢复。`,
            okText: '确认',
            okType: 'danger',
            cancelText: '取消',
            onOk: async () => {
                try {
                    await deleteCase(caseData.id);
                    message.success('删除成功');
                    onClose();
                    onDelete?.();
                } catch (error) {
                    message.error('删除失败');
                }
            },
        });
    };

    // Handle export case
    const handleExport = () => {
        message.info('导出功能开发中');
    };

    // More actions menu
    const moreMenuItems: MenuProps['items'] = [
        canDelete && {
            key: 'delete',
            label: '删除案件',
            icon: <DeleteOutlined />,
            danger: true,
            onClick: handleDelete,
        },
        {
            key: 'export',
            label: '导出案件',
            icon: <ExportOutlined />,
            onClick: handleExport,
        },
    ].filter(Boolean) as MenuProps['items'];

    // Get status color
    const getStatusColor = (status: CaseStatus): string => {
        const colorMap: Record<CaseStatus, string> = {
            [CaseStatus.ACTIVE]: 'blue',
            [CaseStatus.CLOSED]: 'default',
            [CaseStatus.SUSPENDED]: 'orange',
            [CaseStatus.PENDING]: 'gold',
            [CaseStatus.IN_PROGRESS]: 'processing',
            [CaseStatus.WAITING_CLIENT]: 'warning',
            [CaseStatus.WAITING_COURT]: 'cyan',
            [CaseStatus.COMPLETED]: 'success',
            [CaseStatus.CANCELLED]: 'error',
        };
        return colorMap[status] || 'default';
    };

    // Get status text
    const getStatusText = (status: CaseStatus): string => {
        const textMap: Record<CaseStatus, string> = {
            [CaseStatus.ACTIVE]: '进行中',
            [CaseStatus.CLOSED]: '已结案',
            [CaseStatus.SUSPENDED]: '已暂停',
            [CaseStatus.PENDING]: '待处理',
            [CaseStatus.IN_PROGRESS]: '进行中',
            [CaseStatus.WAITING_CLIENT]: '等待客户',
            [CaseStatus.WAITING_COURT]: '等待法院',
            [CaseStatus.COMPLETED]: '已完成',
            [CaseStatus.CANCELLED]: '已取消',
        };
        return textMap[status] || status;
    };

    const handleRefresh = () => {
        loadCaseDetails();
        onUpdate?.();
    };

    return (
        <Drawer
            title={null}
            placement="right"
            width={720}
            open={visible}
            onClose={onClose}
            destroyOnClose
            className="case-drawer"
            closable={false}
            styles={{ body: { padding: 0 } }}
        >
            {loading ? (
                <div style={{ textAlign: 'center', padding: '100px 0' }}>
                    <Spin size="large" />
                </div>
            ) : caseData ? (
                <div className="case-drawer-content">
                    {/* Header */}
                    <div className="case-drawer-header">
                        <div className="case-drawer-header-top">
                            <Space size="middle" style={{ flex: 1 }}>
                                <div className="case-avatar">
                                    {caseData.case_name?.charAt(0) || 'C'}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ marginBottom: '8px' }}>
                                        <h2 className="case-name" style={{ display: 'inline', marginRight: '12px' }}>
                                            {caseData.case_name}
                                        </h2>
                                        <Tag color={getStatusColor(caseData.case_status)}>
                                            {getStatusText(caseData.case_status)}
                                        </Tag>
                                    </div>
                                    <div className="case-meta">
                                        <span>案件编号：{caseData.case_number}</span>
                                        <span style={{ margin: '0 8px' }}>|</span>
                                        <span>负责律师：{caseData.responsible_lawyer_name || '未指定'}</span>
                                    </div>
                                </div>
                            </Space>
                            <Space>
                                {canInvite && (
                                    <Tooltip title="邀请协作">
                                        <Button
                                            type="text"
                                            icon={<TeamOutlined />}
                                            onClick={() => setInviteModalVisible(true)}
                                        />
                                    </Tooltip>
                                )}
                                {canEdit && (
                                    <Tooltip title="设置提醒">
                                        <Button
                                            type="text"
                                            icon={<BellOutlined />}
                                            onClick={() => setReminderModalVisible(true)}
                                        />
                                    </Tooltip>
                                )}
                                {moreMenuItems && moreMenuItems.length > 0 && (
                                    <Dropdown menu={{ items: moreMenuItems }} placement="bottomRight">
                                        <Button type="text" icon={<MoreOutlined />} />
                                    </Dropdown>
                                )}
                                <Tooltip title="关闭">
                                    <Button
                                        type="text"
                                        icon={<CloseOutlined />}
                                        onClick={onClose}
                                    />
                                </Tooltip>
                            </Space>
                        </div>

                        {/* Permission Alert */}
                        {!canEdit && canView && (
                            <Alert
                                message="只读权限"
                                description="您不是此案件的负责律师或助理律师，仅可查看。"
                                type="info"
                                showIcon
                                style={{ marginTop: 16 }}
                            />
                        )}
                    </div>

                    {/* Tabs */}
                    <Tabs
                        activeKey={activeTab}
                        onChange={(key) => {
                            setActiveTab(key);
                            setLoadedTabs(prev => new Set(Array.from(prev).concat(key)));
                        }}
                        className="case-drawer-tabs"
                    >
                        <TabPane tab="案件信息" key="info">
                            <div className="case-drawer-body">
                                <CaseInfoTab
                                    caseData={caseData}
                                    canEdit={canEdit}
                                    onRefresh={handleRefresh}
                                />
                            </div>
                        </TabPane>

                        <TabPane tab="案件跟进" key="followup">
                            <div className="case-drawer-body">
                                {loadedTabs.has('followup') && (
                                    <CaseFollowUpTab
                                        caseId={caseData.id}
                                        canEdit={canEdit}
                                    />
                                )}
                            </div>
                        </TabPane>

                        <TabPane tab="案件大事记" key="milestone">
                            <div className="case-drawer-body">
                                {loadedTabs.has('milestone') && (
                                    <CaseMilestoneTab
                                        caseId={caseData.id}
                                        canEdit={canEdit}
                                    />
                                )}
                            </div>
                        </TabPane>

                        <TabPane tab="财务记录" key="finance">
                            <div className="case-drawer-body">
                                {loadedTabs.has('finance') && (
                                    <CaseFinanceTab
                                        caseId={caseData.id}
                                        caseData={caseData}
                                        canEdit={canManageFinance}
                                    />
                                )}
                            </div>
                        </TabPane>

                        <TabPane tab="附件管理" key="attachment">
                            <div className="case-drawer-body">
                                {loadedTabs.has('attachment') && (
                                    <CaseAttachmentTab
                                        caseId={caseData.id}
                                        canEdit={canEdit}
                                    />
                                )}
                            </div>
                        </TabPane>

                        <TabPane tab="关联线索" key="lead">
                            <div className="case-drawer-body">
                                {loadedTabs.has('lead') && (
                                    <CaseLeadTab leadId={caseData.lead_id || null} />
                                )}
                            </div>
                        </TabPane>
                    </Tabs>
                </div>
            ) : null}

            {/* Invite Collaborator Modal */}
            {inviteModalVisible && caseData && (
                <InviteCollaboratorModal
                    visible={inviteModalVisible}
                    caseId={caseData.id}
                    caseData={caseData}
                    onCancel={() => setInviteModalVisible(false)}
                    onSuccess={() => {
                        setInviteModalVisible(false);
                        handleRefresh();
                    }}
                />
            )}

            {/* Set Reminder Modal */}
            {reminderModalVisible && caseData && (
                <SetReminderModal
                    visible={reminderModalVisible}
                    targetType="case"
                    targetId={caseData.id}
                    onCancel={() => setReminderModalVisible(false)}
                    onSuccess={() => {
                        setReminderModalVisible(false);
                        message.success('提醒设置成功');
                    }}
                />
            )}
        </Drawer>
    );
};

export default CaseDrawer;
